import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Stack,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { useRequestStore, useAttachmentStore, useAuthStore } from '../../stores/root_store';
import { useMessageAttachments } from '../../hooks/useMessageAttachments';
import { useTimelineItems } from '../../hooks/useTimelineItems';
import { MessageComposerAttachments } from '../../components/MessageComposerAttachments';
import { REQUEST_STATUS } from '../../types';
import { MessageList } from '../../components/MessageList';
import { MessageAttachmentChips } from '../../components/MessageAttachmentChips';
import { VoiceRecordingPlayer } from '../../components/VoiceRecordingPlayer';
import { SafeHtml } from '../../components/SafeHtml';
import { StatusChip } from '../../components/StatusChip';
import { RequestDetailsCard, AttachmentsCard, PhoneCallsCard, ClientCalendarCard, RecurringCard } from '../../components/sidebar';
import { requestApi, type ClientInfo } from '../../api/request_api';
import { StarRating } from '../../components/StarRating';
import { fileAssociationApi, type AssociatedFileInfo } from '../../api/file_association_api';
import { PhoneProvider } from '../../contexts/PhoneContext';

const RequestDetailPageInner = observer(() => {
  const { requestKey } = useParams<{ requestKey: string }>();
  const navigate = useNavigate();
  const requestStore = useRequestStore();
  const attachmentStore = useAttachmentStore();
  const authStore = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [clientInfoLoading, setClientInfoLoading] = useState(false);
  const [messageAttachments, setMessageAttachments] = useState<Record<string, AssociatedFileInfo[]>>({});
  const [requestAttachments, setRequestAttachments] = useState<AssociatedFileInfo[]>([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Attachment handling
  const {
    attachments,
    addFiles,
    removeAttachment,
    clearAttachments,
    uploading,
    associateAll,
  } = useMessageAttachments();

  const request = requestKey ? requestStore.getRequestByKey(requestKey) : undefined;
  const messages = request?.key ? requestStore.messages.get(request.key) || [] : [];
  const timelineItems = useTimelineItems(messages, [], 'client');

  // Scroll to bottom when messages change or when navigating to the page
  useEffect(() => {
    // Small delay to ensure DOM is ready after navigation
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [messages.length, requestKey]);

  useEffect(() => {
    const loadRequest = async () => {
      if (requestKey) {
        setLoading(true);
        await requestStore.fetchRequest(requestKey);
        setLoading(false);
      }
    };
    loadRequest();
  }, [requestKey, requestStore]);

  useEffect(() => {
    if (request?.key) {
      requestStore.fetchMessages(request.key, true);
    }
  }, [request?.key, requestStore]);

  // Fetch message attachments when messages change
  useEffect(() => {
    const fetchMessageAttachments = async () => {
      if (messages.length === 0) return;

      const messageUuids = messages.map(m => m.uuid).filter(Boolean);
      if (messageUuids.length === 0) return;

      try {
        const result = await fileAssociationApi.listByAssociations('request_message', messageUuids);
        setMessageAttachments(result.items);
      } catch (err) {
        console.error('Failed to fetch message attachments:', err);
      }
    };
    fetchMessageAttachments();
  }, [messages]);

  // Fetch request-level attachments (for initial email content)
  useEffect(() => {
    const fetchRequestAttachments = async () => {
      if (!request?.uuid) return;

      try {
        const result = await fileAssociationApi.listByAssociations('request', [request.uuid]);
        const allRequestAttachments = result.items[request.uuid] || [];

        // De-duplicate: exclude attachments that are already shown with messages
        const messageFileUuids = new Set(
          Object.values(messageAttachments).flat().map(a => a.file_uuid)
        );
        const filtered = allRequestAttachments.filter(
          a => !messageFileUuids.has(a.file_uuid)
        );
        setRequestAttachments(filtered);
      } catch (err) {
        console.error('Failed to fetch request attachments:', err);
      }
    };
    fetchRequestAttachments();
  }, [request?.uuid, messageAttachments]);

  // Fetch client info when request loads
  useEffect(() => {
    const loadClientInfo = async () => {
      if (requestKey && !clientInfo && !clientInfoLoading) {
        setClientInfoLoading(true);
        try {
          const info = await requestApi.getClientInfo(requestKey);
          setClientInfo(info);
        } catch (err) {
          console.error('Failed to load client info:', err);
        } finally {
          setClientInfoLoading(false);
        }
      }
    };
    loadClientInfo();
  }, [requestKey, clientInfo, clientInfoLoading]);

  // Update sidebar visibility when screen size changes
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const handleRate = async (rating: number) => {
    if (!requestKey) return;
    const updated = await requestApi.rateRequest(requestKey, rating);
    // Use set directly instead of receiveRequestUpdate to avoid duplicate
    // entries from uuid/datastore_key aliases in the Map
    requestStore.requests.set(requestKey, updated);
  };

  const handleCancelRequest = async () => {
    if (!requestKey) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const updated = await requestApi.updateStatus(requestKey, REQUEST_STATUS.CLOSED);
      // Same as handleRate: set directly so the status chip and composer update
      requestStore.requests.set(requestKey, updated);
      setCancelOpen(false);
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel request');
    } finally {
      setCancelling(false);
    }
  };

  const handleToggleArchive = async () => {
    if (!requestKey || !request) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      const updated = await requestApi.setClientArchived(requestKey, !request.client_archived);
      // Same as handleRate: set directly so the button and chip update
      requestStore.requests.set(requestKey, updated);
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Failed to update archive');
    } finally {
      setArchiving(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !requestKey || !request) return;
    if (uploading) return; // Don't send while uploads in progress

    setSending(true);
    const message = await requestStore.sendMessage(requestKey, newMessage.trim());

    if (message) {
      // Associate any uploaded files with both the message and the request
      if (attachments.length > 0) {
        await associateAll([
          { type: 'request_message', uuid: message.uuid },
          { type: 'request', uuid: request.uuid },
        ]);
        // Refresh attachments sidebar
        attachmentStore.fetchByRequest(requestKey);
        // Refresh message attachments inline
        const allMessageUuids = [...messages.map(m => m.uuid), message.uuid].filter(Boolean);
        const result = await fileAssociationApi.listByAssociations('request_message', allMessageUuids);
        setMessageAttachments(result.items);
      }
      setNewMessage('');
      clearAttachments();
    }
    setSending(false);
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, px: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!request) {
    return (
      <Box sx={{ py: 4, px: 2 }}>
        <Alert severity="error">Request not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/request')}
          sx={{ mt: 2 }}
        >
          Back to Requests
        </Button>
      </Box>
    );
  }

  const isOpen = request.status < REQUEST_STATUS.CLOSED;

  return (
    <Box sx={{ display: 'flex', py: 4, px: 2, gap: 2 }}>
      {/* Main Content */}
      <Box sx={{ flex: 1, maxWidth: sidebarOpen ? 'calc(100% - 340px)' : '100%', transition: 'max-width 0.3s' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(request.client_archived ? '/request?filter=archived' : '/request')}
          >
            Back to Requests
          </Button>
          {!authStore.isAssistant && (
            <Button
              variant="outlined"
              onClick={handleToggleArchive}
              disabled={archiving}
              startIcon={
                archiving ? <CircularProgress size={16} color="inherit" />
                  : request.client_archived ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />
              }
            >
              {request.client_archived ? 'Unarchive' : 'Archive'}
            </Button>
          )}
        </Box>
        {archiveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setArchiveError(null)}>
            {archiveError}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Typography variant="h5" component="h1">
                {request.title || 'Untitled Request'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {request.client_archived && <Chip label="Archived" size="medium" variant="outlined" />}
                <StatusChip status={request.status} size="medium" variant="simple" />
              </Box>
            </Box>

            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Created: {new Date(request.date_created).toLocaleString()}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {request.content_type === 'text/html' ? (
              <SafeHtml content={request.content} forceLight />
            ) : (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {request.content}
              </Typography>
            )}

            {request.is_voice_request && (
              <VoiceRecordingPlayer attachments={requestAttachments} />
            )}

            {requestAttachments.filter(a => !a.content_type?.startsWith('audio/')).length > 0 && (
              <MessageAttachmentChips
                attachments={requestAttachments.filter(a => !a.content_type?.startsWith('audio/'))}
              />
            )}
          </CardContent>
        </Card>

        {/* Messages Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Messages
            </Typography>

            {requestStore.messagesLoading && messages.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : messages.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No messages yet. Send a message to get started.
              </Typography>
            ) : (
              <>
                <MessageList items={timelineItems} perspective="client" attachmentsByMessage={messageAttachments} />
                <div ref={messagesEndRef} />
              </>
            )}

            {!isOpen && (
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                This request is closed. Sending a message will re-open it.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: isOpen ? 2 : 0 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder={isOpen ? "Type a message..." : "Type a message to re-open this request..."}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  multiline
                  minRows={3}
                  maxRows={4}
                  disabled={sending}
                />
                <MessageComposerAttachments
                  attachments={attachments}
                  onAddFiles={addFiles}
                  onRemove={removeAttachment}
                  disabled={sending}
                />
              </Box>
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={sending || uploading || !newMessage.trim()}
                sx={{ alignSelf: 'flex-end' }}
              >
                {(sending || uploading) ? <CircularProgress size={24} /> : <SendIcon />}
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        {requestStore.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {requestStore.error}
          </Alert>
        )}
      </Box>

      <Dialog open={cancelOpen} onClose={() => !cancelling && setCancelOpen(false)}>
        <DialogTitle>Cancel this request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{request.title || 'Untitled Request'}" will be closed and work on it will stop.
            Sending a message later will re-open it.
          </DialogContentText>
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={cancelling}>
            Keep request
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCancelRequest}
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Cancel request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sidebar Toggle Button */}
      <IconButton
        onClick={() => setSidebarOpen(!sidebarOpen)}
        sx={{
          position: 'fixed',
          right: sidebarOpen ? 328 : 8,
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'background.paper',
          boxShadow: 1,
          zIndex: 1,
          transition: 'right 0.3s',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {sidebarOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>

      {/* Sidebar */}
      <Collapse in={sidebarOpen} orientation="horizontal">
        <Box sx={{ width: 320, flexShrink: 0 }}>
          <Stack spacing={2}>
            <RequestDetailsCard
              request={request}
              clientInfo={clientInfo}
              loading={clientInfoLoading}
            />
            {request.is_recurring && request.recurring_info && !authStore.isAssistant && (
              <RecurringCard
                request={request}
                onUpdate={() => requestStore.fetchRequest(requestKey!)}
              />
            )}
            {!isOpen && !authStore.isAssistant && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Rate This Request
                  </Typography>
                  <StarRating
                    value={request.star_rating ?? null}
                    onRate={handleRate}
                  />
                </CardContent>
              </Card>
            )}
            {!isOpen && authStore.isAssistant && request.star_rating && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Client Rating
                  </Typography>
                  <StarRating
                    value={request.star_rating}
                    readOnly
                  />
                </CardContent>
              </Card>
            )}
            <ClientCalendarCard requestKey={requestKey!} />
            <AttachmentsCard
              requestKey={requestKey!}
              requestUuid={request.uuid}
              canWork={false}
            />
            <PhoneCallsCard
              requestUuid={request.uuid}
              onOpenDialer={() => {}}
              canWork={false}
            />
            {/* Rarely used, so it sits quietly at the bottom of the sidebar */}
            {isOpen && !authStore.isAssistant && (
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setCancelError(null);
                    setCancelOpen(true);
                  }}
                >
                  Cancel request
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
});

export const RequestDetailPage = observer(() => {
  const { requestKey } = useParams<{ requestKey: string }>();
  // Key by request so navigating between requests resets per-request state
  return (
    <PhoneProvider>
      <RequestDetailPageInner key={requestKey} />
    </PhoneProvider>
  );
});
